import { useEffect, useRef, useState } from "react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type {
  FoundUser,
  Organisation,
  OrganisationId,
  OrganisationType,
  PersonInvolvementKind,
  UserMembership,
} from "@thalamos/haven-common";
import {
  isOrganisationDistantFromPerson,
  isRoleEligibleFor,
  USER_FIND_MIN_TERM_LENGTH,
} from "@thalamos/haven-common";

/**
 * How long to wait after a keystroke before asking the server.
 *
 * Long enough that typing a name is one request rather than eight, short
 * enough that the pause is not read as the page having stopped.
 */
const SETTLE_MS = 250;

/**
 * Somebody found, and the body they would be involved under.
 *
 * The two are chosen in one act rather than in two steps, because a search
 * finds people rather than people-at-an-organisation: the row carries every
 * context the account holds, and picking one is what says which they are being
 * involved under. `membership` is `null` for a guest — somebody with no body
 * behind them, which only a contributing assessor may be.
 */
export type ChosenUser = {
  user: FoundUser;
  membership: UserMembership | null;
};

/**
 * Says that a body has nothing to do with this child's care, and lets the
 * naming proceed anyway.
 *
 * What counts as having nothing to do with it is `isOrganisationDistantFromPerson`, which
 * also says why this never blocks.
 */
export const DistanceWarning = ({
  /** The body they would be involved under, or `null` for a guest. */
  organisation,
}: {
  organisation: string | null;
}) => {
  const { t } = useTranslation();

  return (
    <Alert severity="warning" icon={<WarningAmberIcon fontSize="inherit" />}>
      <Typography variant="body2" fontWeight={500}>
        {t("access.picker.distant")}
      </Typography>
      <Typography variant="body2">
        {organisation === null
          ? t("access.picker.distantGuest")
          : t("access.picker.distantExplanation", { organisation })}
      </Typography>
    </Alert>
  );
};

/**
 * Runs a search when what was typed has settled, and reports what came back.
 *
 * `load` is injected rather than called from here, so a story shows the picker
 * against results it chose and the component keeps no knowledge of the API.
 *
 * It is held in a ref rather than depended on, so a fresh closure on every
 * render does not restart the search — the term is what a new search is worth
 * running for.
 */
export const useSettledResults = <T,>(
  term: string,
  enabled: boolean,
  load: (term: string) => Promise<T[]>,
): { results: T[]; loading: boolean } => {
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const latest = useRef(load);
  latest.current = load;

  useEffect(() => {
    if (!enabled) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    let current = true;

    const timer = setTimeout(() => {
      void latest
        .current(term)
        .then((found) => {
          // A slower earlier search must not overwrite a later one's results,
          // which is what a stale response would otherwise do on every
          // keystroke that shortens the term.
          if (current) {
            setResults(found);
          }
        })
        .catch(() => {
          if (current) {
            setResults([]);
          }
        })
        .finally(() => {
          if (current) {
            setLoading(false);
          }
        });
    }, SETTLE_MS);

    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [term, enabled]);

  return { results, loading };
};

/**
 * One offerable way of involving somebody: the person, and one body behind them
 * — or none, where a guest may take this part.
 */
export type Offer = {
  key: string;
  chosen: ChosenUser;
  label: string;
  eligible: boolean;
  distant: boolean;
};

/**
 * Every way of involving `user` in `kind`. A guest is offered only where the
 * part allows one and `guestLabel` is given to name the offer.
 */
export const offersFor = (
  user: FoundUser,
  kind: PersonInvolvementKind,
  near: readonly OrganisationId[],
  guestLabel?: string,
): Offer[] => {
  const contexts = user.memberships.map((membership) => ({
    key: `${user.id}|${membership.organisationId}|${membership.role}`,
    chosen: { user, membership },
    label: membership.organisationName ?? membership.organisationId,
    // A membership carries a `Role` and `isRoleEligibleFor` answers only for a
    // `CustomerRole`, so the guard is what keeps `thalamos_admin` out — not
    // `isRoleEligibleFor` refusing it.
    eligible:
      membership.role !== "thalamos_admin" &&
      isRoleEligibleFor(membership.role, kind),
    distant: isOrganisationDistantFromPerson({
      organisationId: membership.organisationId,
      near,
    }),
  }));

  // A guest is the sole route by which somebody outside every organisation
  // reaches a child, and is always distant: nothing connects them to this
  // child's care but the naming itself.
  return guestLabel !== undefined && isRoleEligibleFor(null, kind)
    ? [
        ...contexts,
        {
          key: `${user.id}|guest`,
          chosen: { user, membership: null },
          label: guestLabel,
          eligible: true,
          distant: true,
        },
      ]
    : contexts;
};

export type UserPickerProps = {
  /** Which involvement they would take, which decides who is offerable. */
  kind: PersonInvolvementKind;
  value: ChosenUser | null;
  onChange: (chosen: ChosenUser | null) => void;
  onFind: (term: string) => Promise<FoundUser[]>;
  /** The bodies already part of this child's care, plus the acting one. */
  near: readonly OrganisationId[];
};

/**
 * Finds somebody to involve, and says which body they would be involved under.
 *
 * The search refuses to list: nothing is fetched until enough has been typed to
 * name somebody. That is the guard that keeps the workforce of every customer
 * from being paged through, and it is stated here as well as at the server so
 * the field explains itself rather than answering with a refusal.
 */
export const UserPicker = ({
  kind,
  value,
  onChange,
  onFind,
  near,
}: UserPickerProps) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState("");

  const longEnough = term.trim().length >= USER_FIND_MIN_TERM_LENGTH;
  const { results, loading } = useSettledResults(
    term.trim(),
    longEnough,
    onFind,
  );

  if (value !== null) {
    const organisation = value.membership?.organisationName ?? null;

    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" fontWeight={500}>
            {value.user.name}
          </Typography>
          <Chip size="small" label={organisation ?? t("access.guest")} />
          <Button
            size="small"
            onClick={() => {
              onChange(null);
            }}
          >
            {t("access.picker.clear")}
          </Button>
        </Stack>
        {isOrganisationDistantFromPerson({
          organisationId: value.membership?.organisationId ?? null,
          near,
        }) ? (
          <DistanceWarning organisation={organisation} />
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      <TextField
        size="small"
        label={t("access.picker.search")}
        helperText={t("access.picker.searchHint", {
          count: USER_FIND_MIN_TERM_LENGTH,
        })}
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
      />

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t("access.picker.searching")}
          </Typography>
        </Stack>
      ) : null}

      {longEnough && !loading && results.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("access.picker.noResults")}
        </Typography>
      ) : null}

      <List dense disablePadding>
        {results.flatMap((user) =>
          offersFor(user, kind, near, t("access.picker.guest")).map((offer) => (
            <ListItemButton
              key={offer.key}
              disabled={!offer.eligible}
              onClick={() => {
                onChange(offer.chosen);
              }}
            >
              <ListItemText
                primary={user.name}
                secondary={
                  offer.eligible
                    ? offer.label
                    : t("access.picker.ineligible", {
                        organisation: offer.label,
                      })
                }
              />
              {offer.eligible && offer.distant ? (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={t("access.picker.distant")}
                />
              ) : null}
            </ListItemButton>
          )),
        )}
      </List>
    </Stack>
  );
};

export type OrganisationPickerProps = {
  /** The kinds of body that may hold the involvement being recorded. */
  types: readonly OrganisationType[];
  value: Organisation | null;
  onChange: (organisation: Organisation | null) => void;
  onFind: (
    types: readonly OrganisationType[],
    term: string,
  ) => Promise<Organisation[]>;
  near: readonly OrganisationId[];
};

/**
 * Picks the body taking a part in a child's care.
 *
 * The opposite guard to the people picker, deliberately: it lists as soon as it
 * opens, because "show me the local authorities" is how choosing the duty
 * holder works and which bodies those are is a matter of public record. The
 * kinds it may offer come from `organisationTypesEligibleFor`, so it cannot offer an
 * organisation the write would then refuse.
 */
export const OrganisationPicker = ({
  types,
  value,
  onChange,
  onFind,
  near,
}: OrganisationPickerProps) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState("");

  const { results, loading } = useSettledResults(term.trim(), true, (value) =>
    onFind(types, value),
  );

  if (value !== null) {
    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" fontWeight={500}>
            {value.name}
          </Typography>
          <Chip size="small" label={t(`organisation.type.${value.type}`)} />
          <Button
            size="small"
            onClick={() => {
              onChange(null);
            }}
          >
            {t("access.picker.clear")}
          </Button>
        </Stack>
        {isOrganisationDistantFromPerson({ organisationId: value.id, near }) ? (
          <DistanceWarning organisation={value.name} />
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      <TextField
        size="small"
        label={t("access.picker.filter")}
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
      />

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t("access.picker.searching")}
          </Typography>
        </Stack>
      ) : null}

      {!loading && results.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("access.picker.noOrganisations")}
        </Typography>
      ) : null}

      <List dense disablePadding>
        {results.map((organisation) => (
          <ListItemButton
            key={organisation.id}
            onClick={() => {
              onChange(organisation);
            }}
          >
            <ListItemText
              primary={organisation.name}
              secondary={t(`organisation.type.${organisation.type}`)}
            />
            {isOrganisationDistantFromPerson({
              organisationId: organisation.id,
              near,
            }) ? (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={t("access.picker.distant")}
              />
            ) : null}
          </ListItemButton>
        ))}
      </List>
    </Stack>
  );
};
